import React, { useState, useEffect, useCallback } from "react";
import { Route, useNavigate } from "react-router-dom";

import "./PropertyModal.css";
import ContactUsOverlay from "./ContactUsOverlay"; // Import the new overlay
import ScheduleInspectionOverlay from "./ScheduleInspectionOverlay";
import {
  MapPinned,
  Bath,
  Bed,
  Car,
  StretchHorizontal,
  LandPlot,
  Hash,
  BadgeCheck,
  MessageSquare,
  Info,
  Home,
} from "lucide-react";
import { Route as RouteIcon } from "lucide-react";




const PropertyModal = ({ property, isOpen, onClose, onMakeOffer }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isContactOverlayOpen, setContactOverlayOpen] = useState(false); // State for the contact overlay
  const [isInspectionOverlayOpen, setInspectionOverlayOpen] = useState(false);




  // Functions to handle the contact overlay
  const openContactOverlay = () => setContactOverlayOpen(true);
  const closeContactOverlay = () => setContactOverlayOpen(false);

  // Functions to handle the inspection overlay
  const openInspectionOverlay = () => setInspectionOverlayOpen(true);
  const closeInspectionOverlay = () => setInspectionOverlayOpen(false);

  // Only show real property images - no stock/fallback images
  const getCurrentImage = () => {
    // Only use real property images
    if (property?.images && property.images.length > 0 && !imageError) {
      return property.images[currentImageIndex];
    }
    return null;
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    if (!imageError) {
      setImageError(true);
    }
  };

  const openFullscreen = () => setIsFullscreen(true);
  const closeFullscreen = () => setIsFullscreen(false);

  const nextImage = useCallback(() => {
    const images =
      property?.images || (property?.image ? [property.image] : []);
    if (images && images.length > 1) {
      setImageLoading(true);
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
      setImageError(false);
    }
  }, [property]);

  const prevImage = useCallback(() => {
    const images =
      property?.images || (property?.image ? [property.image] : []);
    if (images && images.length > 1) {
      setImageLoading(true);
      setCurrentImageIndex(
        (prev) => (prev - 1 + images.length) % images.length
      );
      setImageError(false);
    }
  }, [property]);

  useEffect(() => {
    setCurrentImageIndex(0);
    setImageError(false);
    setImageLoading(true);
  }, [property]);


  // Lock background scroll when modal is open (especially on mobile)
  useEffect(() => {
    if (isOpen) {
      const prevOverflow = document.body.style.overflow;
      const prevTouch = document.body.style.touchAction;
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
      return () => {
        document.body.style.overflow = prevOverflow;
        document.body.style.touchAction = prevTouch;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowLeft":
          prevImage();
          break;
        case "ArrowRight":
          nextImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, property, nextImage, prevImage]);

  // Create Google Maps URL for the property address
  const createGoogleMapsUrl = () => {
    const address = `${property?.address || ""}, ${property?.suburb || ""}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
  };

  // Create Google Maps directions URL
  const createDirectionsUrl = () => {
    const address = `${property?.address || ""}, ${property?.suburb || ""}`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      address
    )}`;
  };

  // Handle schedule viewing click
  const handleScheduleViewing = () => {
    openInspectionOverlay();
  };

  // PDF helper functions
  const isPdf = (url) => {
    if (!url) return false;
    return url.toLowerCase().split("?")[0].endsWith(".pdf");
  };

  const getMediaUrl = (url) => {
    if (!url) return url;
    if (isPdf(url)) {
      // Chrome/Edge/Adobe standard PDF viewer parameters
      // Use # to append parameters for the native viewer
      // Ensure we don't have multiple fragments and that it comes after query params
      const baseUrl = url.split('#')[0];
      return `${baseUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&page=1`;
    }
    return url;
  };




  // Price formatter (AUD) similar to card component
  const formatPrice = (priceStr) => {
    if (!priceStr || priceStr === "Price on request") return priceStr;
    const numbers = priceStr.toString().replace(/[^0-9]/g, "");
    const numPrice = parseInt(numbers);
    if (Number.isNaN(numPrice)) return priceStr;
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numPrice);
  };

  const displayStatus = (() => {
    const avail = property?.availability?.toLowerCase().trim();

    if (avail === "sold") {
      return "SOLD";
    }

    return "FOR SALE";
  })();

  const currentImage = getCurrentImage();
  const hasImages =
    (property?.images && property.images.length > 0) || property?.image;
  const hasMultipleImages = property?.images && property.images.length > 1;

  // For single image properties, create a temporary array for consistency
  const displayImages =
    property?.images || (property?.image ? [property.image] : []);

  // Handle PDF loading state fallback
  // Native PDF viewers in iframes don't always trigger onLoad reliably
  useEffect(() => {
    if (isOpen && currentImage && isPdf(currentImage)) {
      // Give it a tiny bit of time to start the iframe then hide loading,
      // as PDFs handle their own internal loading UI
      const timer = setTimeout(() => {
        setImageLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentImage]);

  if (!isOpen || !property) return null;

  // Debug logging for property description
  console.log("🔍 PropertyModal property:", {
    address: property.address,
    hasDescription: !!property.description,
    descriptionLength: property.description?.length || 0,
    descriptionPreview:
      property.description?.substring(0, 50) + "..." || "No description",
  });

  // Debug logging
  console.log("🖼️ PropertyModal Debug:", {
    hasImages,
    hasMultipleImages,
    imagesLength: property?.images?.length,
    displayImagesLength: displayImages.length,
    currentImageIndex,
    imageLoading,
  });

  const modalStyle = {
    zIndex: property?.availability?.toLowerCase() === "sold" ? 9999 : 1000,
  };

  const features = [];
  if (property.landSize) {
    features.push(
      <div className="feature-item-simple" key="landSize">
        <LandPlot size={20} />
        <span>{property.landSize} sqm</span>
      </div>
    );
  }
  if (property.buildSize) {
    features.push(
      <div className="feature-item-simple" key="buildSize">
        <Home size={20} />
        <span>{property.buildSize} sqm</span>
      </div>
    );
  }
  if (
    // property.propertyType &&
    // property.propertyType.trim().toLowerCase().includes("land") &&
    // !property.propertyType.trim().toLowerCase().includes("home") &&
    property.registrationConstructionStatus
  ) {
    features.push(
      <div className="feature-item-simple" key="regStatus">
        <BadgeCheck size={20} />
        <span>{property.registrationConstructionStatus}</span>
      </div>
    );
  }

  if (typeof property.frontage === "number" && property.frontage > 0) {
    features.push(
      <div className="feature-item-simple" key="frontage">
        <StretchHorizontal size={20} />
        <span>{property.frontage} m frontage</span>
      </div>
    );
  }
  if (property.bedrooms > 0) {
    features.push(
      <div className="feature-item-simple" key="bedrooms">
        <Bed size={20} />
        <span>{property.bedrooms}</span>
      </div>
    );
  }
  if (property.bathrooms > 0) {
    features.push(
      <div className="feature-item-simple" key="bathrooms">
        <Bath size={20} />
        <span>{property.bathrooms}</span>
      </div>
    );
  }
  if (property.parking > 0) {
    features.push(
      <div className="feature-item-simple" key="parking">
        <Car size={20} />
        <span>{property.parking}</span>
      </div>
    );
  }
  if (property.remark) {
    features.push(
      <div className="feature-item-simple" key="lot">
        <MessageSquare size={20} />
        <span>{property.remark}</span>
      </div>
    );
  }

  return (
    <div
      className="property-modal-overlay"
      onClick={onClose}
      style={modalStyle}
    >
      <div className="property-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="modal-content">
          {property.availability === "sold" && (
            <div className="sold-overlay"></div>
          )}
          <div className="modal-left">
            <div className="property-image-section">
              <div className="property-modal-status">{displayStatus}</div>
              {/* Loading state */}
              {imageLoading && !imageError && (
                <div className="image-loading-placeholder">
                  <div className="loading-spinner">⏳</div>
                  <p>Loading images...</p>
                </div>
              )}

              {/* No images state */}
              {!hasImages && !imageError && !imageLoading && (
                <div className="no-images-placeholder">
                  <div className="no-images-icon">🏠</div>
                  <p>No images available</p>
                </div>
              )}

              {/* media display (Image or PDF) */}
              {currentImage && (
                isPdf(currentImage) ? (
                  <div
                    className="pdf-viewer-container"
                    style={{
                      display: imageLoading ? "none" : "flex",
                      height: "100%",
                      width: "100%"
                    }}
                  >
                    <iframe
                      src={getMediaUrl(currentImage)}
                      title="Property PDF"
                      className="media-pdf"
                      onLoad={() => setImageLoading(false)}
                      scrolling="no"
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        borderRadius: "8px",
                        overflow: "hidden"
                      }}
                    />
                  </div>
                ) : (
                  <img
                    src={currentImage}
                    alt={property.address}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    className="modal-property-image"
                    style={{ display: imageLoading ? "none" : "block" }}
                    onClick={openFullscreen}
                  />
                )
              )}


              {/* Navigation arrows (for multiple images only) */}
              {hasMultipleImages && (

                <>
                  <button
                    className="slideshow-nav-btn prev-btn"
                    onClick={prevImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="slideshow-nav-btn next-btn"
                    onClick={nextImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </>
              )}

              {/* Image counter removed per design */}
            </div>
          </div>

          <div className="modal-right">
            <div className="property-details">
              {/* 3. Price Section */}
              {property.propertyType && (
                <div className="property-price-section">
                  <div className="price-and-type-container">
                    <span className="type-badge btn-secondary">
                      {property.propertyType === "Home and Land Packages"
                        ? "HOME & LAND"
                        : property.propertyType?.toUpperCase()}
                    </span>
                    {property.priceCustomerVisibility === "1" &&
                      property.price && (
                        <div className="property-price">
                          {formatPrice(property.price)}
                        </div>
                      )}
                  </div>
                </div>
              )}
              {/* 1. Address & Suburb Section */}
              <div className="property-header-simple">
                <h2
                  className="property-address-modal"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                  }}
                >
                  {property.lot && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                      }}
                    >
                      <MapPinned size={20} color="#b91c1c" />
                      <span>Lot {property.lot},</span>
                    </span>
                  )}
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <span>
                      {property.address}, {property.suburb}
                    </span>
                  </span>
                </h2>
              </div>

              {/* 2. Property Features - Horizontal */}
              <div className="property-features-horizontal">
                {features.map((feature, index) => (
                  <React.Fragment key={index}>
                    {feature}
                    {index < features.length - 1 && (
                      <span className="detail-separator">|</span>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* 4. Type & Offer Button Section */}
              {property?.availability?.toLowerCase() !== "sold" && (
                <div className="type-and-offer-section">
                  <button className="btn btn-secondary" onClick={onMakeOffer}>
                    Make an Offer
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleScheduleViewing}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2V5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 2V5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 10H21"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Schedule Inspection
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={openContactOverlay}
                  >
                    <Info className="icon" size={20} color="#f5f5dc" />
                    More Info
                  </button>
                </div>
              )}

              {/* 6. Bottom Action Buttons */}
              <div className="action-buttons"></div>
            </div>

            <div className="map-section">
              <h3>Location</h3>
              <div className="map-and-actions-wrapper">
                <div className="map-container">
                  <iframe
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                      `${property.address}, ${property.suburb}`
                    )}`}
                    width="100%"
                    height="150"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Property Location"
                  />
                </div>
                <div className="map-actions">
                  <a
                    href={createGoogleMapsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                  >
                    <MapPinned className="icon" size={22} color="#f5f5dc" />
                    Open in Google Maps
                  </a>
                  <a
                    href={createDirectionsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                  >
                    <RouteIcon className="icon" size={22} color="#f5f5cd" />
                    Get Directions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isFullscreen && (
        <div
          className="property-modal-overlay"
          onClick={(e) => {
            e.stopPropagation();
            closeFullscreen();
          }}
        >
          <div
            className="property-modal"
            style={{ background: "transparent", boxShadow: "none" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-btn"
              onClick={(e) => {
                e.stopPropagation();
                closeFullscreen();
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={currentImage}
                alt={property.address}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "90vh",
                  objectFit: "contain",
                  background: "transparent",
                  borderRadius: 0,
                }}
              />
              {hasMultipleImages && (
                <>
                  <button
                    className="slideshow-nav-btn prev-btn"
                    onClick={prevImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="slideshow-nav-btn next-btn"
                    onClick={nextImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {/* Counter removed in fullscreen as well */}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <ContactUsOverlay
        isOpen={isContactOverlayOpen}
        onClose={closeContactOverlay}
      />
      <ScheduleInspectionOverlay
        property={property}
        isOpen={isInspectionOverlayOpen}
        onClose={closeInspectionOverlay}
      />
    </div>
  );
};

export default PropertyModal;
